import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import {
  formatSeenTime,
  normalizeWebActivityDurationBasis,
  webActivityCloseReasonLabel,
  webActivityDurationDisplay,
  webActivityEvidenceChipColor,
  webActivityEvidenceChipLabel,
  webActivityEvidenceLabel,
  webActivityEvidenceTooltip,
  webActivityPageTitle,
  webActivityReportEvidenceSummary,
  webActivityUrlDisplay,
} from '../../utils/monitoringPresentation';
import { apiClient } from '../../services/api';
import MonitoringFilters, {
  MonitoringFilterScope,
  useMonitoringFilterQuery,
} from '../../components/workforceMonitoring/MonitoringFilters';
import SortableHeader from '../../components/workforceMonitoring/SortableHeader';
import { SortDir, sortRows, toggleSort } from '../../utils/tableSort';

export interface WebActivityPageProps {
  scope?: MonitoringFilterScope;
  lockedDeviceId?: string;
}

function evidenceDetailTooltip(row: any): string {
  const basisTip = webActivityEvidenceTooltip(row.duration_basis);
  const close = webActivityCloseReasonLabel(row.close_reason);
  const version = typeof row.extension_version === 'string' && row.extension_version.trim()
    ? `Extension v${row.extension_version.trim()}`
    : null;
  return [basisTip, close ? `Ended: ${close}` : null, version].filter(Boolean).join(' ');
}

const WebActivityPage: React.FC<WebActivityPageProps> = ({
  scope = 'global',
  lockedDeviceId,
}) => {
  // Structured Web Activity is release-gated. On the standalone route, redirect to
  // Applications when the feature is disabled so no empty page is shown via a deep link.
  const { data: configData } = useQuery({
    queryKey: ['monitoring', 'configuration', 'nav'],
    queryFn: () => apiClient.monitoring.getConfiguration(),
    staleTime: 60_000,
  });
  // Only decide once the config has actually resolved, so we never flash a redirect
  // before the feature flag is known.
  const configResolved = configData !== undefined;
  const webActivityEnabled = Boolean((configData as any)?.data?.data?.web_activity_feature_enabled);
  const isStandalone = scope === 'global' && !lockedDeviceId;

  const [filters, setFilters] = useMonitoringFilterQuery(scope, lockedDeviceId);

  const { data, isLoading } = useQuery({
    queryKey: ['monitoringWebActivity', scope, filters],
    queryFn: () => apiClient.monitoring.getWebActivity({
      date: filters.date,
      employee_id: filters.employeeId || undefined,
      device_id: filters.deviceId || undefined,
      campaign_id: filters.campaignId || undefined,
      domain: filters.domain || undefined,
      browser: filters.browser || undefined,
    }),
  });

  const [sortBy, setSortBy] = React.useState('last_seen');
  const [sortDir, setSortDir] = React.useState<SortDir>('desc');

  const apiRows: any[] = data?.data?.data || [];
  const meta = (data?.data as any)?.meta;
  const filteredRows = React.useMemo(() => {
    if (!filters.evidence) return apiRows;
    return apiRows.filter(
      (row) => normalizeWebActivityDurationBasis(row.duration_basis) === filters.evidence,
    );
  }, [apiRows, filters.evidence]);

  const rows = sortRows(filteredRows, sortBy, sortDir, (row: any, key) => {
    if (key === 'first_seen' || key === 'last_seen' || key === 'start_time' || key === 'end_time') {
      const value = row[key] ?? row.first_seen ?? row.last_seen;
      return value ? new Date(value).getTime() : 0;
    }
    if (key === 'duration_seconds') {
      return Number(row.duration_seconds ?? row.active_duration_seconds ?? 0);
    }
    return row[key];
  });

  const reportSummary = webActivityReportEvidenceSummary(apiRows, meta?.duration_basis);
  const emptyMessage = scope === 'computer'
    ? 'No browser activity recorded for this computer.'
    : 'No browser activity recorded.';
  const colSpan = scope === 'global' ? 10 : 9;
  const onSort = (column: string) => {
    const next = toggleSort(sortBy, sortDir, column);
    setSortBy(next.key);
    setSortDir(next.dir);
  };

  if (isStandalone && configResolved && !webActivityEnabled) {
    return <Navigate to="/workforce-monitoring/applications" replace />;
  }

  return (
    <Box>
      <MonitoringFilters
        scope={scope}
        value={filters}
        onChange={setFilters}
        lockedDeviceId={lockedDeviceId}
        showDomain
        showBrowser
        showEvidence
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Active Duration is time TEKS TIME has evidence the website was actively used. Foreground-verified
        intervals use Companion browser correlation (idle and lock excluded). Tab open/close lifetime alone
        does not count as active use.
      </Typography>
      {apiRows.length > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Report evidence:
          </Typography>
          <Tooltip title={reportSummary.tooltip}>
            <Chip
              size="small"
              label={reportSummary.label}
              color={reportSummary.mixed ? 'default' : webActivityEvidenceChipColor(apiRows[0]?.duration_basis)}
              variant={reportSummary.mixed ? 'outlined' : 'filled'}
              aria-label={`Report evidence: ${reportSummary.label}`}
            />
          </Tooltip>
        </Box>
      )}
      <Box mb={2}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const params = new URLSearchParams({ date: filters.date });
            if (filters.employeeId) params.set('employee_id', filters.employeeId);
            if (filters.deviceId) params.set('device_id', filters.deviceId);
            if (filters.campaignId) params.set('campaign_id', filters.campaignId);
            window.location.href = `/api/monitoring/export/web-activity?${params.toString()}`;
          }}
        >
          Export CSV
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeader label="Domain" column="domain" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Page" column="page_title" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Employee" column="employee_name" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Campaign" column="campaign" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              {scope === 'global' && (
                <SortableHeader label="Computer" column="computer" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              )}
              <SortableHeader label="Browser" column="browser" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Active Duration" column="duration_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <TableCell>Evidence</TableCell>
              <SortableHeader label="Start" column="first_seen" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="End" column="last_seen" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={colSpan}><CircularProgress size={20} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row: any, index: number) => {
              const basis = normalizeWebActivityDurationBasis(row.duration_basis);
              const url = webActivityUrlDisplay(row.full_url);
              const durationText = webActivityDurationDisplay(basis, row.duration_label);
              const evidenceLabel = webActivityEvidenceChipLabel(basis);
              const evidenceAria = webActivityEvidenceLabel(basis);
              return (
                <TableRow key={`${row.employee_id}-${row.domain}-${row.start}-${index}`}>
                  <TableCell>{row.domain || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{webActivityPageTitle(row.page_title, row.domain)}</Typography>
                    {url ? (
                      <Typography variant="caption" color="text.secondary" display="block" title={url}>
                        URL: {url}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{row.employee_name || '—'}</TableCell>
                  <TableCell>{row.campaign || '—'}</TableCell>
                  {scope === 'global' && <TableCell>{row.computer || '—'}</TableCell>}
                  <TableCell>{row.browser || '—'}</TableCell>
                  <TableCell>{durationText}</TableCell>
                  <TableCell>
                    <Tooltip title={evidenceDetailTooltip(row)}>
                      <Chip
                        size="small"
                        label={evidenceLabel}
                        color={webActivityEvidenceChipColor(basis)}
                        variant={basis === 'FOREGROUND_VERIFIED' ? 'filled' : 'outlined'}
                        aria-label={evidenceAria}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatSeenTime(row.start_time || row.first_seen)}</TableCell>
                  <TableCell>{formatSeenTime(row.end_time || row.last_seen)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default WebActivityPage;
